import React, { createRef, FC, useContext, useEffect, useState } from 'react';
import { bind } from '@react-rxjs/core';
import { accessTokenInfo$, isAuthorized$ } from '../oauth/oauth';
import { useIsRestoring, useQuery, useQueryClient } from '@tanstack/react-query'
import SinglePresenterMeeting from './SinglePresenterMeeting';
import { Alert } from '@fluentui/react-components/unstable';
import LinkRouter from '../LinkRouter';
import { nodeServer } from '../constants';
import { Button, Card, CardHeader, Field, InfoLabel, Spinner, Switch, Text } from '@fluentui/react-components';
import { isAdmin$ } from '../store/userStore';
import Enumerable from 'linq';
import { ToastContext } from '../context/ToastContext';
import { Collapse } from "@fluentui/react-motion-components-preview";
import { compareMeetingChanges, convertMeetingData, MeetingData } from './meetingUtils';
import { AdminContext } from '../context/AdminContext';

interface MeetingsProps {

}

//export const queryClient = new QueryClient();

const [useIsAuthorized] = bind(isAuthorized$, isAuthorized$.value);
const [useIsAdmin] = bind(isAdmin$, isAuthorized$.value);

export const Meetings: FC<MeetingsProps> = () => {

    const authorized = useIsAuthorized();
    const isAdmin = useIsAdmin();
    const isRestoring = useIsRestoring();
    const toastContext = useContext(ToastContext);
    const adminModeContext = useContext(AdminContext);
    const queryClient = useQueryClient();

    const containerRef = createRef<HTMLDivElement>();

    const { data: meetingData, status } = useQuery({
        queryKey: ['meetingData'], refetchOnWindowFocus: false, queryFn: async () => {

            let url = nodeServer + `meetings/current`;
            const res = await fetch(url);
            if (!res.ok) {
                throw new Error('Network response was not ok')
            }
            const raw = await res.json();
            const meetingData: MeetingData[] = [];
            const editedMeetingData: MeetingData[] = [];
            for (const line of raw) {
                //assumes type = 0
                let item: MeetingData = JSON.parse(line.data);
                item.id = line.id;
                item.date = new Date(item.date);
                item.time = new Date(item.time);


                meetingData.push(item);
                editedMeetingData.push(structuredClone(item));

            }

            setEditedMeetingData(e => {
                setPreviousEditedMeetingData(e);
                return editedMeetingData;
            });

            return meetingData;
        }
    });

    const { data: pastMeetingData, status: statusPast } = useQuery({
        queryKey: ['pastMeetingData'], refetchOnWindowFocus: false, queryFn: async () => {

            let url = nodeServer + `meetings/past`;
            const res = await fetch(url);
            if (!res.ok) {
                throw new Error('Network response was not ok')
            }
            const raw = await res.json();
            const oldMeetingData: MeetingData[] = [];
            for (const line of raw) {
                //assumes type = 0
                let item: MeetingData = JSON.parse(line.data, (k, v) => {
                    if (k === 'date' || k === 'time') {
                        return new Date(Date.parse(v));
                    } else {
                        return v;
                    }
                });
                item.id = line.id;
                oldMeetingData.push(item);
            }

            return oldMeetingData;
        }
    });

    const uploadImageAsync = async (file: File): Promise<string | null> => {
        if (accessTokenInfo$.value !== null && accessTokenInfo$.value.accessToken !== null) {
            let headers = new Headers();
            //headers.append('Content-Type', 'multipart/form-data');
            //headers.append('Content-Type', "");
            headers.append('Authorization', "Bearer " + accessTokenInfo$.value.accessToken);
            var form_data = new FormData();
            form_data.append('file', file);
            form_data.append('location', 'meetings');
            const response = await fetch(nodeServer + "uploadMeetingImage", { method: 'POST', body: form_data, headers: headers });
            const json = await response.json();
            if (json.error) {
                return json.error;
            } else {
                return json.url;
            }
        } else {
            return "Not authenticated!";
        }
    };

    //const [oldMeetingData, setOldMeetingData] = useState<MeetingData[]>([]);


    const [previousEditedMeetingData, setPreviousEditedMeetingData] = useState<MeetingData[]>([]);
    const [editedMeetingData, setEditedMeetingData] = useState<MeetingData[]>([]);

    const [editMode, setEditMode] = useState<boolean>(false);
    const onChangeEditMode = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setEditMode(e.currentTarget.checked);
        },
        [setEditMode]
    );

    const [changed, setChanged] = useState<boolean>(false);

    const [pastMeetingsVisible, setPastMeetingsVisible] = React.useState<boolean>(false);

    const handleChange = (p: MeetingData) => {
        // console.log(p);
        // console.log(p.time.toLocaleTimeString('en-US',{hour:'numeric',minute:'2-digit',hour12:true}));
        const foundIndex = editedMeetingData.findIndex(v => v.id === p.id);
        if (foundIndex !== -1) {
            const edited = editedMeetingData.map((v, i) => {
                if (i === foundIndex) {
                    return p;
                } else {
                    return v;
                }
            });
            //setEditedMeetingData(edited);
            setEditedMeetingData(e => {
                setPreviousEditedMeetingData(e);
                return edited;
            });

        } else {
            console.log("This should never be -1.");
        }

    };

    const onAddMeeting = (_: React.MouseEvent<HTMLButtonElement>) => {
        const meeting: MeetingData = {
            id: (Math.random() * -1),
            title: "",
            date: new Date(Date.now()),
            time: new Date(Date.now()),
            //dateTime: new Date(Date.now()),
            component: "SinglePresenterMeeting",
            imgSrc: "",
            registerLink: "",
            presenters: [{
                name: '',
                details: '',
                hash: Math.floor(Math.random() * Date.now()).toString(36)
            }],
            content: ""

        }
        setEditedMeetingData(e => {
            setPreviousEditedMeetingData(e);
            return [...editedMeetingData, meeting];
        });

        setChanged(true);
    };

    const onAddPresenter = (meetingId: number) => {
        setEditedMeetingData(e => {
            setPreviousEditedMeetingData(e);
            const meeting = e.find(x=>x.id === meetingId);
            if (meeting){
                meeting.presenters.push({name: "", details: "", hash: Math.floor(Math.random() * Date.now()).toString(36)});
            }
            return [...e];
        });
    };

    const onDeletePresenter = (meetingId: number, presenterIndex: number) => {
        setEditedMeetingData(e => {
            console.log(meetingId);
            console.log(presenterIndex);
            setPreviousEditedMeetingData(e);
            const meeting = e.find(x=>x.id === meetingId);
            if (meeting){
                console.log(meeting.presenters[presenterIndex]);
                meeting.presenters.splice(presenterIndex,1);
            }
            return [...e];
        });
    };

    useEffect(() => {
        if (meetingData) {
            const same = compareMeetingChanges(meetingData!, editedMeetingData);
            setChanged(!same);
        }
    }, [editedMeetingData, meetingData]);


    useEffect(() => {
        if (editedMeetingData.length > previousEditedMeetingData.length && previousEditedMeetingData.length !== 0) {
            const parentElement = containerRef.current?.parentElement;
            if (parentElement)
                parentElement.scrollTo(0, parentElement.scrollHeight - 500);
        }
    }, [editedMeetingData, previousEditedMeetingData, containerRef]);

    const onDeleteMeeting = (v: MeetingData) => {
        let copy = [...editedMeetingData];
        const index = copy.findIndex(x => x.id === v.id);
        copy.splice(index, 1);

        //const same = compareMeetingChanges(meetingData!, copy);
        //setEditedMeetingData(copy);
        setEditedMeetingData(e => {
            setPreviousEditedMeetingData(e);
            return copy;
        });

        //setChanged(!same);
    };


    const onSave = async (_: React.MouseEvent<HTMLButtonElement>) => {
        //get changes
        const enumEdited = Enumerable.from(editedMeetingData);
        const enumOriginal = Enumerable.from(meetingData!);
        const adds = enumEdited.except(enumOriginal, v => v.id);
        const removes = enumOriginal.except(enumEdited, v => v.id);
        const potentialChanged = enumOriginal.except(removes);
        // const removeAndAdd = enumEdited.except(adds).where(x => potentialChanged.any(y => y.id == x.id && (y.title !== x.title || y.details !== x.details || y.photo !== x.photo || y.email !== x.email)))
        const updates = enumEdited.except(adds).where(x => potentialChanged.any(y => y.id === x.id && (y.title !== x.title || y.date !== x.date || y.time !== x.time || y.registerLink !== x.registerLink || y.imgSrc !== x.imgSrc || y.content !== x.content)))


        if (accessTokenInfo$.value !== null && accessTokenInfo$.value.accessToken !== null) {
            let headers = new Headers();
            headers.append('Content-Type', 'application/json');
            headers.append('Authorization', 'Bearer ' + accessTokenInfo$.value.accessToken);

            const removeResults: Promise<any>[] = [];
            removes.forEach(async x => {
                const y = convertMeetingData(x);
                const task = fetch(nodeServer + "meetings/" + y.id, { method: 'DELETE', headers: headers })
                    .then((res) => res.json());
                removeResults.push(task);
            });
            const addResults: Promise<any>[] = [];
            adds.forEach(async x => {
                const y = convertMeetingData(x);
                y.id = undefined;
                const task = fetch(nodeServer + "meetings", { method: 'POST', body: JSON.stringify(y), headers: headers })
                    .then((res) => res.json());
                addResults.push(task);
            });
            const updateResults: Promise<any>[] = [];
            updates.forEach(async x => {
                const y = convertMeetingData(x);
                const task = fetch(nodeServer + "meetings/" + y.id, { method: 'PUT', body: JSON.stringify(y), headers: headers })
                    .then((res) => res.json());
                updateResults.push(task);
            });

            const result = await Promise.all(removeResults.concat(addResults).concat(updateResults));
            const errors: string[] = [];
            result.forEach(x => {
                if (x.error) {
                    errors.push(x.error);
                }
            });
            console.log(result);
            if (errors.length === 0) {
                setChanged(false);
                toastContext.notifySaveSuccess("The changes to the meetings page have been saved.")
                await queryClient.invalidateQueries({ queryKey: ['meetingData', 'pastMeetingData'] });
            } else {
                errors.forEach(x => {
                    toastContext.notifyError(x);
                })
            }
        }

    };

    return (
        <>
            {isAdmin && adminModeContext &&
                <Card>
                    <CardHeader header={<InfoLabel info={"This box only appears to admins when Admin Mode is checked."}>Admin Tools</InfoLabel>} />

                    <Switch label="Edit Mode" checked={editMode} onChange={onChangeEditMode}></Switch>
                    {editMode &&
                        <>
                            <Button appearance='secondary' onClick={e => onAddMeeting(e)}>Add New Meeting</Button>
                            <Button appearance='primary' disabled={!changed} onClick={onSave}>Save</Button>
                            <Text>Only delete mistakes or cancellations.  Do NOT delete old meetings.
                                They should gracefully disappear one day after their expiration date is set.  </Text>
                        </>
                    }
                </Card>
            }
            <div ref={containerRef} style={{ display: 'flex', flexDirection: 'column' }}>
                {
                    status !== "success" || isRestoring || editedMeetingData === undefined ? (
                        <>
                            <Spinner size='extra-large' />
                        </>)
                        :
                        editedMeetingData.length === 0 ?

                            (<div>
                                <Text size={500}>There are no scheduled meetings for the near future yet. </Text>
                            </div>)

                            :
                            editedMeetingData.map((v, i) => {
                                switch (v.component) {
                                    case "SinglePresenterMeeting":
                                        return (

                                            <SinglePresenterMeeting
                                                isEditing={editMode}
                                                onChange={handleChange}
                                                uploadImageAsync={uploadImageAsync}
                                                onDeleteMeeting={onDeleteMeeting}
                                                onAddPresenter={onAddPresenter}
                                                onDeletePresenter={onDeletePresenter}
                                                key={i}
                                                {...v} />
                                        );
                                    default:
                                        return "NO MATCH";
                                }
                            })
                }

                <Field >
                    <Switch
                        label="Show Recent Meetings"
                        checked={pastMeetingsVisible}
                        onChange={() => setPastMeetingsVisible((v) => !v)}
                    />
                </Field>
                <Collapse visible={pastMeetingsVisible}>
                    <div >
                        {
                            statusPast !== "success" || isRestoring || pastMeetingData === undefined ? (
                                <>
                                    <Spinner size='extra-large' />
                                </>)
                                :

                                pastMeetingData.map((v, i) => {
                                    //console.log(v);
                                    switch (v.component) {
                                        case "SinglePresenterMeeting":
                                            return (
                                                <SinglePresenterMeeting key={i} {...v} />
                                            );
                                        default:
                                            return "NO MATCH";
                                    }
                                })
                        }
                    </div>
                </Collapse>
                <LinkRouter as="ButtonLink" href={"/archives"} disabled={!authorized} appearance='secondary' >Meeting Archives</LinkRouter>
                {!authorized &&
                    <Alert intent="warning">Archives are reserved for members of 2YC3.</Alert>
                }
            </div>

        </>
    );
};

